import * as React from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

export interface OrganizationOption {
  id: string;
  name: string;
}

interface OrganizationComboboxProps {
  organizations: OrganizationOption[];
  value: string;
  onChange: (value: string) => void;
  allLabel?: string;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  triggerClassName?: string;
}

const ALL_VALUE = "all";

export const OrganizationCombobox: React.FC<OrganizationComboboxProps> = ({
  organizations,
  value,
  onChange,
  allLabel = "All Organizations",
  placeholder = "Search organizations…",
  disabled,
  className,
  triggerClassName,
}) => {
  const [open, setOpen] = React.useState(false);

  const selected = value === ALL_VALUE
    ? { id: ALL_VALUE, name: allLabel }
    : organizations.find((org) => org.id === value);

  const handleSelect = (next: string) => {
    onChange(next);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            "justify-between font-normal",
            triggerClassName ?? "w-full sm:w-[250px]",
            className,
          )}
        >
          <span className="truncate">{selected ? selected.name : allLabel}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[--radix-popover-trigger-width] p-0"
        align="start"
      >
        <Command>
          <CommandInput placeholder={placeholder} />
          <CommandList>
            <CommandEmpty>No organizations found.</CommandEmpty>
            <CommandGroup>
              <CommandItem
                value={`${allLabel} ${ALL_VALUE}`}
                onSelect={() => handleSelect(ALL_VALUE)}
              >
                <Check
                  className={cn(
                    "mr-2 h-4 w-4",
                    value === ALL_VALUE ? "opacity-100" : "opacity-0",
                  )}
                />
                {allLabel}
              </CommandItem>
              {organizations.map((org) => (
                <CommandItem
                  key={org.id}
                  value={`${org.name} ${org.id}`}
                  onSelect={() => handleSelect(org.id)}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      value === org.id ? "opacity-100" : "opacity-0",
                    )}
                  />
                  <span className="truncate">{org.name}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export default OrganizationCombobox;
